/**
 * Derive an itinerary's inclusions + exclusions from the components it actually
 * contains, instead of asking the sales agent to type them. Whatever is present
 * (flights, hotel, activities, transfers, visa, meals, insurance) becomes an
 * inclusion line; whatever is absent becomes an exclusion. Pure + deterministic
 * so the builder preview and the published customer snapshot always agree.
 *
 * Destination-specific wording comes later; for now these are sensible generic
 * lines matching the legacy quote text.
 */

/** Minimal component shape needed for the derivation (subset of the full row). */
export interface InclusionComponent {
  kind: string; // ItineraryComponentKind: FLIGHT | HOTEL | TRANSFER | ACTIVITY | VAS | CRUISE
  title: string;
  fromLocation?: string | null;
  toLocation?: string | null;
  visaId?: string | null;
  mealServiceId?: string | null;
  /**
   * Kind-specific extras. Only read for CRUISE rows, where it carries
   * `shipName` / `nights` / `boardBasis` for the cruise inclusion line. Typed
   * as `unknown` because the caller's component row types it loosely; the
   * derivation narrows it null-safely.
   */
  metaJson?: unknown;
}

export interface InclusionItinerary {
  numberOfNights?: number | null;
  visaId?: string | null;
  /**
   * ADR-0062. Processing mode of the picked visa master row (null when no
   * visa is attached OR the caller hasn't wired this through yet). Drives
   * the wording of the visa inclusion line + an extra "embassy fees payable
   * at counter" exclusion for APPOINTMENT_EMBASSY visas.
   */
  visaProcessingMode?: 'STANDARD' | 'APPOINTMENT_ONLY' | 'APPOINTMENT_EMBASSY' | null;
  insuranceWanted?: boolean | null;
  /** When true, visa is not applicable - omit visa lines from both lists. */
  isDomestic?: boolean | null;
}

export interface DerivedInclusions {
  inclusions: string[];
  exclusions: string[];
}

/**
 * Tuning for the derivation. FIT (the default) derives every category from the
 * components present, as before. FD bakes transfers / activities / flights /
 * visa into the day briefs + a curated template, NOT as component rows, so for
 * FD we:
 *  - never emit Transfer or Activity lines (the FD template owns them);
 *  - never emit the hotel-accommodation lines (the template owns them);
 *  - keep airfare component-driven, taxes, and the generic exclusions;
 *  - treat visa / insurance as included (so NOT excluded) when the curated
 *    inclusions already mention them, since the FD template is the source of
 *    truth for what the land package covers.
 */
export interface DeriveOptions {
  format?: 'FIT' | 'FD';
  /** The FD template's curated inclusion lines - the visa/insurance signal. */
  curatedInclusions?: string[];
}

/**
 * The FD-supplement kind stamped on a VAS component's metaJson (VISA / INSURANCE
 * / OTHER / VEHICLE). Lets the derivation treat a visa/insurance supplement as
 * "visa/insurance included" even though it carries no visaId master link.
 */
function fdSupplementKind(c: InclusionComponent): string | null {
  const m =
    c.metaJson && typeof c.metaJson === 'object' ? (c.metaJson as Record<string, unknown>) : null;
  return m && typeof m.fdSupplementKind === 'string' ? m.fdSupplementKind : null;
}

function dedupe(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const s = raw.trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

interface FlightSegmentMeta {
  origin?: unknown;
  destination?: unknown;
  originCity?: unknown;
  destinationCity?: unknown;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** "Pune (PNQ)", or whichever half is known. */
function place(city: unknown, code: unknown): string | null {
  const c = str(city);
  const k = str(code);
  if (c && k) return `${c} (${k})`;
  return c ?? k;
}

/** One journey's segments -> "Pune (PNQ) to Osaka (KIX), via Delhi". */
function journeyLabel(segments: unknown): string | null {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const segs = segments.filter((s): s is FlightSegmentMeta => Boolean(s) && typeof s === 'object');
  const first = segs[0];
  const last = segs[segs.length - 1];
  if (!first || !last) return null;
  const from = place(first.originCity, first.origin);
  const to = place(last.destinationCity, last.destination);
  if (!from || !to) return null;
  const stops = segs
    .slice(0, -1)
    .map((s) => str(s.destinationCity) ?? str(s.destination))
    .filter((v): v is string => Boolean(v));
  return `${from} to ${to}${stops.length > 0 ? `, via ${stops.join(', ')}` : ''}`;
}

/**
 * Customer-readable route for a FLIGHT component, built from the segments the
 * flight search stored on metaJson (city names, not bare IATA codes). Null when
 * the component carries no segments, so the caller falls back to from/to.
 */
function flightRouteLabel(c: InclusionComponent): string | null {
  const m =
    c.metaJson && typeof c.metaJson === 'object' ? (c.metaJson as Record<string, unknown>) : null;
  if (!m) return null;
  const out = journeyLabel(m.outbound);
  if (!out) return null;
  const back = journeyLabel(m.returns);
  return back ? `${out}; return ${back}` : out;
}

export function deriveInclusionsExclusions(
  components: InclusionComponent[],
  itin: InclusionItinerary,
  opts: DeriveOptions = {},
): DerivedInclusions {
  const inclusions: string[] = [];
  const exclusions: string[] = [];
  const of = (kind: string): InclusionComponent[] => components.filter((c) => c.kind === kind);
  const isFd = opts.format === 'FD';
  // FD treats its curated inclusions as the truth for what the land package
  // covers. A flight / visa / insurance line there means it is included, so the
  // matching auto exclusion must be suppressed in favour of the curated line.
  const curatedMentions = (re: RegExp): boolean =>
    (opts.curatedInclusions ?? []).some((line) => re.test(line));

  // Flights - list each configured route so the customer sees exactly what flies.
  const flights = of('FLIGHT');
  if (flights.length > 0) {
    inclusions.push('Return airfare as per the itinerary');
    for (const f of flights) {
      const route =
        flightRouteLabel(f) ??
        (f.fromLocation && f.toLocation ? `${f.fromLocation} - ${f.toLocation}` : f.title);
      if (route) inclusions.push(`Flight: ${route}`);
    }
  } else if (!(isFd && curatedMentions(/flight|airfare/i))) {
    // FD: a flight line in the curated template means flights are included
    // (bundled in the FD, not added as components), so skip the exclusion.
    exclusions.push('Airfare / flight tickets');
  }

  // Accommodation - hotels and/or cruise stays. A cruise stay is accommodation
  // too, so the "Hotel accommodation" exclusion is emitted only when NEITHER a
  // hotel NOR a cruise covers the trip (previously any cruise-only trip wrongly
  // showed "Hotel accommodation" as excluded despite being billed a cabin).
  // Skipped entirely for FD: accommodation is bundled into the land package and
  // spelled out in the curated template, so neither line is auto-derived.
  if (!isFd) {
    const hasHotel = of('HOTEL').length > 0;
    const cruises = of('CRUISE');
    if (hasHotel) {
      const n = itin.numberOfNights ?? 0;
      inclusions.push(
        n > 0 ? `${n} night${n === 1 ? '' : 's'} hotel accommodation` : 'Hotel accommodation',
      );
    }
    // One inclusion line per cruise, naming the ship + board basis.
    for (const c of cruises) {
      const meta =
        c.metaJson && typeof c.metaJson === 'object'
          ? (c.metaJson as Record<string, unknown>)
          : null;
      const shipName =
        meta && typeof meta.shipName === 'string' && meta.shipName.trim()
          ? meta.shipName.trim()
          : c.title;
      const nights = meta && typeof meta.nights === 'number' ? meta.nights : null;
      const boardBasis =
        meta && typeof meta.boardBasis === 'string' && meta.boardBasis.trim()
          ? meta.boardBasis.trim()
          : null;
      const nightLabel =
        nights && nights > 0 ? `${nights} night${nights === 1 ? '' : 's'} cruise` : 'Cruise';
      inclusions.push(`${nightLabel} aboard ${shipName}${boardBasis ? ` (${boardBasis})` : ''}`);
    }
    if (!hasHotel && cruises.length === 0) {
      exclusions.push('Hotel accommodation');
    }
  }

  // Activities / sightseeing - each by name. Skipped for FD: activities live in
  // the day briefs + curated template, never auto-added.
  if (!isFd) {
    for (const a of of('ACTIVITY')) if (a.title) inclusions.push(a.title);
  }

  // Transfers. Skipped for FD: transfers are baked into the FD day briefs, so
  // they are neither auto-included nor ever auto-excluded.
  if (!isFd) {
    if (of('TRANSFER').length > 0) inclusions.push('Transfers as per the itinerary');
    else exclusions.push('Transfers');
  }

  // Visa - domestic trips skip visa entirely. International trips list visa as
  // an inclusion when attached, otherwise as an exclusion. Visa can come from a
  // global visa master (visaId) OR an FD "Visa" supplement (a VAS component
  // whose own title is already an inclusion line, so we only suppress the
  // exclusion for it - no duplicate generic line).
  if (!itin.isDomestic) {
    const hasGlobalVisa = Boolean(itin.visaId) || of('VAS').some((c) => Boolean(c.visaId));
    const hasFdVisaSupplement = of('VAS').some((c) => fdSupplementKind(c) === 'VISA');
    if (hasGlobalVisa) {
      const mode = itin.visaProcessingMode ?? null;
      if (mode === 'APPOINTMENT_ONLY') {
        inclusions.push('Visa (appointment-based) as per the itinerary');
      } else if (mode === 'APPOINTMENT_EMBASSY') {
        inclusions.push('Visa (appointment + embassy visit) as per the itinerary');
        // Called out as an exclusion so the customer isn't surprised at the
        // counter - the master price only covers the vendor's processing fee.
        exclusions.push('Embassy fees payable at counter (not included in visa price)');
      } else {
        inclusions.push('Visa as per the itinerary');
      }
    }
    // FD: a visa line in the curated template means the land package covers it,
    // so suppress the auto "Visa fees" exclusion even without a visaId / supplement.
    if (!hasGlobalVisa && !hasFdVisaSupplement && !(isFd && curatedMentions(/visa/i))) {
      exclusions.push('Visa fees');
    }
  }

  // Meals - a VAS meal component.
  if (of('VAS').some((c) => Boolean(c.mealServiceId))) {
    inclusions.push('Meals as per the itinerary');
  }

  // Travel insurance - global master (insuranceWanted) OR an FD "Insurance"
  // supplement (its own title is already an inclusion line).
  const hasFdInsuranceSupplement = of('VAS').some((c) => fdSupplementKind(c) === 'INSURANCE');
  if (itin.insuranceWanted) inclusions.push('Travel insurance');
  // FD: an insurance line in the curated template means it is covered, so
  // suppress the auto "Travel insurance" exclusion.
  if (
    !itin.insuranceWanted &&
    !hasFdInsuranceSupplement &&
    !(isFd && curatedMentions(/insurance/i))
  ) {
    exclusions.push('Travel insurance');
  }

  // Always applicable.
  inclusions.push('All applicable taxes and GST');

  // Standard exclusions that always apply.
  exclusions.push('Anything not specified under inclusions');
  exclusions.push('Personal expenses (tips, laundry, telephone calls, etc.)');

  return { inclusions: dedupe(inclusions), exclusions: dedupe(exclusions) };
}

/** Matches a curated exclusion line that is about flights / airfare. */
const FLIGHT_EXCLUSION_RE = /\b(flights?|air ?fares?|air ?tickets?)\b/i;

/**
 * FD templates usually exclude "International Flight from India" because the
 * land package does not cover it. Once the agent adds FLIGHT components to the
 * itinerary, flights ARE included, so the curated flight exclusion would
 * contradict the inclusions list. Drop it at merge time (the stored list is
 * left untouched, so removing the flights brings the line back).
 */
export function dropCoveredFdExclusions(
  curatedExclusions: string[],
  components: Array<Pick<InclusionComponent, 'kind'>>,
): string[] {
  if (!components.some((c) => c.kind === 'FLIGHT')) return curatedExclusions;
  return curatedExclusions.filter((line) => !FLIGHT_EXCLUSION_RE.test(line));
}
